import { useEffect, useState } from "react";
import { Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminGetUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } from "./adminApi";
import { useAuth } from "@/context/AuthContext";
import { Text, Select, Modal } from "./ui";
import { Header } from "./AdminTours";

const ROLES = ["admin", "editor", "content_writer"];
const blank = { email: "", name: "", role: "content_writer", password: "" };

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminGetUsers().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.email || !editing.name) { toast.error("Name and email required"); return; }
    if (!editing.id && !editing.password) { toast.error("Password required for new user"); return; }
    setSaving(true);
    try {
      if (editing.id) await adminUpdateUser(editing.id, { name: editing.name, role: editing.role, password: editing.password || undefined });
      else await adminCreateUser(editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const del = async (u) => { if (!window.confirm(`Delete ${u.email}?`)) return; try { await adminDeleteUser(u.id); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } };
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  return (
    <div>
      <Header title="Users" count={items.length} onNew={() => setEditing({ ...blank })} newLabel="Add User" />
      <div className="bg-white border border-[#d4af37]/25">
        {items.map((u) => (
          <div key={u.id} className="flex items-center gap-4 px-5 py-4 border-b border-[#d4af37]/15 last:border-0">
            <div className="w-10 h-10 rounded-full bg-[#7c1d1d] text-white grid place-items-center font-serif">{u.name?.[0]?.toUpperCase() || "U"}</div>
            <div className="flex-1"><div className="font-medium text-[#3b1a1a]">{u.name}</div><div className="text-sm text-[#5a4b4b]">{u.email}</div></div>
            <span className="text-[10px] uppercase tracking-[0.12em] bg-[#14523d]/15 text-[#14523d] px-2 py-1">{u.role}</span>
            <button onClick={() => setEditing({ ...blank, ...u, password: "" })} className="w-8 h-8 grid place-items-center text-[#14523d] hover:bg-[#14523d] hover:text-white"><Edit className="w-4 h-4" /></button>
            {u.id !== user?.id && <button onClick={() => del(u)} className="w-8 h-8 grid place-items-center text-[#7c1d1d] hover:bg-[#7c1d1d] hover:text-white"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit User" : "Add User"}>
        {editing && (
          <div className="space-y-4">
            <Text label="Name *" value={editing.name} onChange={(v) => set("name", v)} />
            <Text label="Email *" value={editing.email} onChange={(v) => set("email", v)} type="email" />
            <Select label="Role" value={editing.role} onChange={(v) => set("role", v)} options={ROLES} />
            <Text label={editing.id ? "New Password (leave blank to keep)" : "Password *"} type="password" value={editing.password} onChange={(v) => set("password", v)} />
            <p className="text-[11px] text-[#5a4b4b]">admin: full access · editor: manage & delete content · content_writer: create & edit content</p>
            <div className="flex gap-3"><button onClick={save} disabled={saving} data-testid="save-user" className="bg-[#7c1d1d] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60">{saving ? "Saving…" : "Save"}</button><button onClick={() => setEditing(null)} className="border border-[#d4af37]/40 px-8 py-3 text-xs tracking-[0.2em] uppercase">Cancel</button></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
